import analytics, { getTrackedProductsValue, shouldTrackMarketingEvent } from '.';
import { getTenancy } from '../../hooks/useTenancy';
import { hasTargetingConsent } from '../consent';
import { typedWindow } from '../Window';
import { ECOM_EVENT_ORDER_COMPLETED, SegmentPurchaseEventProperties } from './events';

/**
 * Vibe (vibe.co) pixel for the Ultra TV campaign; index.html loads it on OneTrust consent (C0004).
 * Conversions defer via onTargetingConsent so ones firing pre-consent queue instead of dropping;
 * page views fire only once consent is granted (no deferral — they'd otherwise burst on late consent).
 *
 * @see https://help.vibe.co/en/articles/6530668-vibe-pixel
 */

/** Non-test production only. */
function vibeEnabled(): boolean {
  return getTenancy() !== 'test' && process.env.REACT_APP_ENVIRONMENT === 'production';
}

/** Fire a conversion, deferred to consent so pre-consent events queue instead of dropping. */
function fireVibeConversion(command: string, ...args: any[]) {
  if (!vibeEnabled()) {
    return;
  }
  typedWindow.onTargetingConsent?.(() => typedWindow.vbpx?.(command, ...args));
}

function vibeOrderCompleted(_messageId: string, properties: SegmentPurchaseEventProperties) {
  // USD only (Vibe has no currency param); gate before computing the value.
  if (!vibeEnabled() || properties.currency !== 'USD') {
    return;
  }

  fireVibeConversion('event', 'purchase', {
    price_usd: getTrackedProductsValue(properties),
    purchase_id: properties.orderId,
  });
}

// Map Segment events to Vibe handlers
type VibeEventHandler = (messageId: string, properties: any) => void;
const VIBE_EVENT_MAP: Record<string, VibeEventHandler> = {
  [ECOM_EVENT_ORDER_COMPLETED]: vibeOrderCompleted,
};

function onTrack(event: string, properties: any, messageId: string) {
  const handler = VIBE_EVENT_MAP[event];
  if (!handler || !shouldTrackMarketingEvent(properties)) {
    return;
  }
  handler(messageId, properties);
}

/** Lead event on checkout email capture, alongside the other pixels' hooks. */
export function trackVibeLead() {
  fireVibeConversion('event', 'lead');
}

/** page_view on SPA nav (initial view comes from index.html); fires only if consent is granted. */
export function vibeTrackPageView() {
  if (!vibeEnabled() || !hasTargetingConsent()) {
    return;
  }
  typedWindow.vbpx?.('event', 'page_view');
}

/** Wire Order Completed → Vibe purchase; safe to call pre-consent. */
export function vibeSetupCallbacks() {
  analytics.onTrack(onTrack);
}
